import { Tabs, TabsProps } from "antd";
import { useMemo } from "react";
import Icon, { ICON_NAME } from "../../../../components/Icon";
import { SitePoint } from "../../../../components/Operation/Steps/SiteStep";
import PauseSection from "../../../../components/TrackingOp/_components/Pause/PauseSection";
import PauseView from "../../../../components/TrackingOp/_components/Pause/PauseView";
import { datetime } from "../../../../util/library/datetime";
import { KeyOfStep, listOfStep, SHORT_DATETIME_FORMAT } from "../../../../util/varibles/constants";
import { groupPausesByCrewTask } from "../../../../util/varibles/global";
import { IOperation2, IService2 } from "../../../../util/varibles/interface";

interface IProps {
    data: IOperation2<IService2>
}

export default function ActivityLog({ data }: IProps) {
    const { activity_log, operation: { crew_tasks = [], site, pauses = [] } } = data;
    const start = activity_log[0];
    const finish = activity_log[activity_log.length - 1];

    const items: TabsProps['items'] = useMemo(() => {
        const rs = [];
        rs.push({
            label: 'PLAN ACTIVITIES LOG',
            key: '1',
            children: <div className="flex flex-col items-center w-full">
                <div className="flex flex-col items-center">
                    <div className="text-[14px]">{datetime(start.est_start_time).format(SHORT_DATETIME_FORMAT)}</div>
                    <SitePoint name={site?.name || ""} />
                </div>
                {crew_tasks.flatMap((item, i) => [
                    <div className="flex flex-col items-center" key={'arrow' + i}>
                        <Icon
                            className="mt-3"
                            icon={ICON_NAME.ARROW_DOWN_3}
                            bg="colorDisable"
                        />
                        <Icon
                            className="mt-[-2px] mb-3"
                            icon={ICON_NAME.ARROW_DOWN_3}
                            bg="colorDisable"
                        />
                    </div>,
                    <div key={item.id} className="flex flex-col items-center">
                        <div className="text-[14px]">{datetime(item.est_start_time).format(SHORT_DATETIME_FORMAT)}</div>
                        <div className='text-[15px] font-[600] text-[--color1]'>{item.description}</div>
                        <div className="text-[14px]">{datetime(item.est_finish_time).format(SHORT_DATETIME_FORMAT)}</div>
                    </div>
                ])}
                <Icon
                    className="mt-3"
                    icon={ICON_NAME.ARROW_DOWN_3}
                    bg="colorDisable"
                />
                <Icon
                    className="mt-[-2px] mb-3"
                    icon={ICON_NAME.ARROW_DOWN_3}
                    bg="colorDisable"
                />
                <div className="flex flex-col items-center">
                    <div className="text-[14px]">{datetime(finish.est_start_time).format(SHORT_DATETIME_FORMAT)}</div>
                    <div className='text-[15px] font-[600] text-[--color1]'>{listOfStep[KeyOfStep.COMPLETED].name}</div>
                </div>
            </div>,
        })

        const sortedCrewTasks = [...crew_tasks].sort((a, b) => {
            if (!a.real_start_time && !b.real_start_time) return 0;
            if (!a.real_start_time) return 1;
            if (!b.real_start_time) return -1;

            return a.real_start_time - b.real_start_time;
        });
        const grouped = groupPausesByCrewTask(pauses, sortedCrewTasks);

        rs.push({
            label: 'ACTUAL ACTIVITIES LOG',
            key: '2',
            children: <div className="flex flex-col items-center w-full">
                <div className="grid grid-cols-[55%_max-content] items-center gap-x-4 gap-y-2 w-full pb-[42px]">
                    <div />

                    <div className="w-full flex justify-center">
                        <div className="flex flex-col items-center">
                            <div>
                                {start.real_start_time
                                    ? datetime(start.real_start_time).format(SHORT_DATETIME_FORMAT)
                                    : "-"}
                            </div>

                            <SitePoint name={site?.name || ""} />
                        </div>
                    </div>

                    {crew_tasks.flatMap((item, i) => {
                        const { before, inside } = grouped[i] || {};

                        return [
                            <div key={`before-pause-${item.id}`}>
                                {before && (<PauseSection>
                                    {before.map(pauseIndex => (
                                        <PauseView key={`pause-${pauseIndex}`} data={pauses[pauseIndex]} />
                                    ))}
                                </PauseSection>)}
                            </div>,

                            <div key={`arrow-${item.id}`} className="w-full flex flex-col items-center">
                                <Icon
                                    className="mt-3"
                                    icon={ICON_NAME.ARROW_DOWN_3}
                                    bg="colorDisable"
                                />

                                <Icon
                                    className="mt-[-2px] mb-3"
                                    icon={ICON_NAME.ARROW_DOWN_3}
                                    bg="colorDisable"
                                />
                            </div>,

                            <div key={`inside-pause-${item.id}`} className="my-auto"                            >
                                {inside && (
                                    <PauseSection>
                                        {inside.map(pauseIndex => (
                                            <PauseView key={`pause-${pauseIndex}`} data={pauses[pauseIndex]} />
                                        ))}
                                    </PauseSection>
                                )}
                            </div>,

                            <div key={`task-${item.id}`} className="w-full flex justify-center"                            >
                                <div className="flex flex-col items-center">
                                    <div>
                                        {item.real_start_time
                                            ? datetime(item.real_start_time).format(SHORT_DATETIME_FORMAT)
                                            : "-"}
                                    </div>

                                    <div>{item.description}</div>

                                    <div>
                                        {item.real_finish_time
                                            ? datetime(item.real_finish_time).format(SHORT_DATETIME_FORMAT)
                                            : "-"}
                                    </div>
                                </div>
                            </div>,
                        ];
                    })}

                    {!!grouped[crew_tasks.length]?.after && <PauseSection>
                        {grouped[crew_tasks.length].after.map(pauseIndex => (
                            <PauseView key={`pause-${pauseIndex}`} data={pauses[pauseIndex]} />
                        ))}
                    </PauseSection>}

                    <div className="w-full flex flex-col items-center">
                        <Icon
                            className="mt-3"
                            icon={ICON_NAME.ARROW_DOWN_3}
                            bg="colorDisable"
                        />

                        <Icon
                            className="mt-[-2px] mb-3"
                            icon={ICON_NAME.ARROW_DOWN_3}
                            bg="colorDisable"
                        />
                    </div>

                    {/* Complete */}
                    <div />

                    <div className="w-full flex justify-center">
                        <div className="flex flex-col items-center">
                            <div>
                                {finish.real_start_time
                                    ? datetime(finish.real_start_time).format(
                                        SHORT_DATETIME_FORMAT
                                    )
                                    : "-"}
                            </div>

                            <div className="text-[15px] font-[600] color-third">
                                {listOfStep[KeyOfStep.COMPLETED].name}
                            </div>
                        </div>
                    </div>
                </div>
            </div>,
        })
        return rs;
    }, [crew_tasks, finish.est_start_time, finish.real_start_time, pauses, site?.name, start.est_start_time, start.real_start_time]);

    return <Tabs
        defaultActiveKey="1"
        size="small"
        type="card"
        style={{ marginBottom: 32 }}
        items={items}
    />
}
